<script setup lang="ts">
  import { getCurrentInstance, onMounted } from "vue";
  import { storeToRefs } from "pinia";
  import { FileText, Play, X } from "lucide-vue-next";
  import { IndexTable, IndexTableName, IndexTableDate, IndexTableEmpty } from "@/components/episto-ui/index-table";
  import type { DataTableColumn } from "@/components/episto-ui/data-table";
  import { ActionsDropdown } from "@/components/episto-ui/actions-dropdown";
  import { StatusBadge } from "@/components/episto-ui/status-badge";
  import { Button } from "@/components/ui/button";
  import { Switch } from "@/components/ui/switch";
  import { Tooltip } from "@/components/ui/tooltip";
  import { useJobsStore } from "@/stores/job/jobs";
  import { useRowNavigation } from "@/composables/useRowNavigation";
  import type { BadgeVariants } from "@/components/ui/badge";
  import type { Job } from "@/types";

  const jobsStore = useJobsStore();
  const { jobs, jobsCount, currentPage, isNew, actionDropdownJobs } = storeToRefs(jobsStore);
  const { changePage, toggleActiveJob, runJob, cancelJob } = jobsStore;
  const { openRow } = useRowNavigation();

  const instance = getCurrentInstance();
  onMounted(() => {
    if (!instance) return;
    const cable = instance.proxy?.$cable;
    cable?.subscribe({ channel: "JobsStatusChannel" });
    cable?._addChannel("JobsStatusChannel", jobsStore.jobsChannelHandlers(cable), {
      _uid: instance.uid,
      _value: instance.proxy,
    });
  });

  const columns: DataTableColumn<Job>[] = [
    { key: "name", label: "Job" },
    { key: "status", label: "Status", width: 110 },
    { key: "active", label: "Active", width: 80 },
    { key: "last_update", label: "Last run", width: 150 },
    { key: "cron", label: "Cron", width: 130 },
  ];

  const STATUS_VARIANTS: Record<string, BadgeVariants["variant"]> = {
    Complete: "success",
    Failed: "danger",
    Queued: "warning",
    Working: "info",
    Cancelled: "neutral",
  };
  const isRunning = (job: Job) => ["Queued", "Working"].includes(job.status);
  const spreadsheetId = (job: Job) => job.job_attributes?.spreadsheet_id ?? "";

  const openJob = (job: Job, event: MouseEvent | KeyboardEvent) => {
    isNew.value = false;
    openRow({ name: "JobEdit", params: { id: job.id } }, event);
  };
</script>

<template>
  <IndexTable
    :items="jobs"
    :columns="columns"
    :current-page="currentPage"
    :total="jobsCount"
    actions-class="w-[190px]"
    empty-message="No job yet."
    @row-click="openJob"
    @page-change="changePage">
    <template #cell-name="{ item }">
      <IndexTableName :name="item.name" />
    </template>
    <template #cell-status="{ item }">
      <StatusBadge
        :status="item.status || 'No job running'"
        :variants="STATUS_VARIANTS" />
    </template>
    <template #cell-active="{ item }">
      <Tooltip
        content="Cron is not defined so the job cannot be active and automatically run"
        :disabled="!!item.cron">
        <span
          class="inline-flex"
          @click.stop>
          <Switch
            :model-value="item.active"
            :disabled="!item.cron"
            @update:model-value="toggleActiveJob({ ...item, active: $event })" />
        </span>
      </Tooltip>
    </template>
    <template #cell-last_update="{ item }">
      <IndexTableDate
        :date="item.last_update"
        format="DD.MM.YYYY HH:mm"
        class="tabular-nums" />
    </template>
    <template #cell-cron="{ item }">
      <Tooltip
        :content="item.cron_description"
        :disabled="!item.cron">
        <span
          v-if="item.cron"
          class="font-mono text-xs"
          >{{ item.cron }}</span
        >
        <IndexTableEmpty v-else />
      </Tooltip>
    </template>
    <template #actions="{ item }">
      <div
        class="flex items-center justify-end gap-1"
        @click.stop>
        <Button
          v-if="!isRunning(item)"
          variant="ghost"
          size="sm"
          class="text-primary"
          @click="runJob(item.id)">
          <Play />
          Run
        </Button>
        <Button
          v-else
          variant="ghost"
          size="sm"
          class="text-primary"
          @click="cancelJob(item.id)">
          <X />
          Cancel
        </Button>
        <Tooltip
          content="Open the spreadsheet"
          :disabled="!spreadsheetId(item)">
          <Button
            variant="ghost"
            size="icon-sm"
            as="a"
            target="_blank"
            :href="`https://docs.google.com/spreadsheets/d/${spreadsheetId(item)}`"
            :class="!spreadsheetId(item) && 'pointer-events-none opacity-40'"
            :tabindex="spreadsheetId(item) ? undefined : -1"
            aria-label="Spreadsheet">
            <FileText />
          </Button>
        </Tooltip>
        <ActionsDropdown
          :actions="actionDropdownJobs"
          :token="item.id.toString()" />
      </div>
    </template>
  </IndexTable>
</template>
